import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUser, useClerk } from "@clerk/react";
import {
  Building2,
  PlusCircle,
  Menu,
  X,
  Compass,
  TrendingUp,
  Receipt,
  BarChart3,
  Settings,
  ShieldCheck,
  FileText,
  LogOut,
  ChevronRight,
  User,
  Sparkles
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

export default function AppLayout({ profile, businessInfo, onResetCategory, onSignOut, children }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuOpenedAt, setMenuOpenedAt] = useState(0);

  const handleLogout = async () => {
    try {
      if (signOut) await signOut();
    } catch (e) {}
    try {
      localStorage.removeItem("bme_custom_token");
      localStorage.removeItem("bme_category_selected");
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    if (onSignOut) {
      onSignOut();
    }
    localStorage.clear();
    window.location.replace("/sign-in");
  };

  const handleOpenMenu = () => {
    setMenuOpenedAt(Date.now());
    setIsMenuOpen(true);
  };

  const hallName = businessInfo?.business_name || "Grand Royal Palace & Convention Hall";
  const ownerName = businessInfo?.contact_person || profile?.full_name || "Rajesh Sharma";

  const menuRows = [
    {
      title: "List on Hall Finder",
      icon: Compass,
      path: "/hall-finder",
      testId: "menu-item-hall-finder"
    },
    {
      title: "Revenue",
      icon: TrendingUp,
      path: "/revenue",
      testId: "menu-item-revenue"
    },
    {
      title: "Financial Details",
      icon: Receipt,
      path: "/financials",
      testId: "menu-item-financial-details"
    },
    {
      title: "Reports",
      icon: BarChart3,
      path: "/reports",
      testId: "menu-item-reports"
    },
    {
      title: "Business Settings",
      icon: Settings,
      path: "/settings",
      testId: "menu-item-business-settings",
      subtitle: "Includes Profile & Hall Setup"
    },
    {
      title: "Privacy Policy",
      icon: ShieldCheck,
      path: "/privacy-policy",
      testId: "menu-item-privacy-policy"
    },
    {
      title: "Terms & Conditions",
      icon: FileText,
      path: "/terms",
      testId: "menu-item-terms"
    }
  ];

  const handleMenuRowClick = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-[#fff1f2]/40 text-gray-900 flex flex-col font-sans">
      {/* Top Header Bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-pink-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Left: Brand Logo & Title */}
            <div className="flex items-center gap-3">
              <Link
                to="/calendar"
                data-testid={TEST_IDS.brandLogo}
                className="flex items-center gap-2.5 group"
              >
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-[#500724] via-[#831843] to-[#be185d] flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                  <Building2 className="w-5 h-5 text-pink-100" />
                </div>
                <div>
                  <span className="text-lg font-black tracking-tight text-pink-950 block leading-tight">
                    Book<span className="text-pink-600">My</span>Events
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-extrabold text-pink-700 bg-pink-50 px-1.5 py-0.2 rounded">
                    Business Portal
                  </span>
                </div>
              </Link>
            </div>

            {/* Middle: Clean Tag */}
            <div className="hidden md:flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-50/80 border border-pink-100 text-xs font-bold text-pink-950">
              <Sparkles className="w-3.5 h-3.5 text-pink-700" />
              <span>Convention Hall Management</span>
            </div>

            {/* Right: + New Booking CTA & Three-Line Menu Button */}
            <div className="flex items-center gap-2.5 sm:gap-3">
              <Link
                to="/bookings/new"
                data-testid={TEST_IDS.btnNewBooking}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs sm:text-sm font-bold shadow-md hover:shadow-lg transition-all"
              >
                <PlusCircle className="w-4 h-4" />
                <span>New Booking</span>
              </Link>

              {/* Three-Line Menu Button */}
              <button
                type="button"
                data-testid="btn-hamburger-menu"
                onClick={handleOpenMenu}
                title="Open Owner Menu"
                className="p-2 sm:p-2.5 rounded-xl border border-pink-200 bg-pink-50/60 hover:bg-pink-100 text-pink-950 transition-colors flex items-center justify-center shadow-sm"
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-pink-950" />
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* Main Full-Screen Page Container */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-pink-100 py-6 text-center text-xs text-gray-500">
        <p className="font-semibold text-pink-950">BookMyEvents Business Portal</p>
        <p className="text-[11px] text-gray-400 mt-1">
          Cloud-synchronized with BookMyEvents mobile app • Powered by Bharath Developers
        </p>
      </footer>

      {/* SLIDE-OUT MOBILE APP STYLE MENU PANEL */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50" data-testid="hamburger-menu-overlay">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity z-40"
            onClick={(e) => {
              if (Date.now() - menuOpenedAt > 250) {
                setIsMenuOpen(false);
              }
            }}
          />

          {/* Menu Drawer on Right */}
          <div
            data-testid="owner-menu-panel"
            className="fixed top-0 right-0 bottom-0 w-full max-w-sm sm:max-w-md bg-white h-full shadow-2xl flex flex-col z-50 border-l border-pink-200 overflow-hidden"
          >
            {/* Top Drawer Header (Hall Name, Owner Name & Close Button) */}
            <div className="bg-gradient-to-r from-pink-950 via-pink-900 to-rose-900 p-6 text-white flex items-start justify-between shadow-md">
              <div className="space-y-1 pr-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/20 text-[10px] font-bold uppercase tracking-wider text-pink-200">
                  <Building2 className="w-3 h-3" />
                  <span>Convention Hall</span>
                </div>
                <h3 className="text-base sm:text-lg font-black leading-snug line-clamp-2">
                  {hallName}
                </h3>
                <p className="text-xs text-pink-100 font-semibold flex items-center gap-1.5 pt-0.5">
                  <User className="w-3.5 h-3.5 text-pink-300" />
                  <span>{ownerName}</span>
                </p>
              </div>

              <button
                type="button"
                data-testid="close-menu-btn"
                onClick={() => setIsMenuOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Links List (One row each with Icon and Arrow) */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1 divide-y divide-gray-100">
              <div className="space-y-1 pb-2">
                {menuRows.map((row) => {
                  const Icon = row.icon;
                  const isActive = location.pathname === row.path;

                  return (
                    <button
                      key={row.title}
                      type="button"
                      data-testid={row.testId}
                      onClick={() => handleMenuRowClick(row.path)}
                      className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all text-left ${
                        isActive
                          ? "bg-pink-50 border border-pink-200 text-pink-950 font-black shadow-xs"
                          : "hover:bg-pink-50/60 text-gray-800 font-bold"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                            isActive
                              ? "bg-pink-900 text-white"
                              : "bg-pink-100/70 text-pink-900"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <span className="text-xs sm:text-sm block">{row.title}</span>
                          {row.subtitle && (
                            <span className="text-[10px] text-gray-400 font-normal block">
                              {row.subtitle}
                            </span>
                          )}
                        </div>
                      </div>

                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>

              {/* Red Sign Out Row at Bottom */}
              <div className="pt-2">
                <button
                  type="button"
                  data-testid="menu-item-signout"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between p-3.5 rounded-2xl hover:bg-rose-50 text-rose-700 font-black transition-all text-left group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center group-hover:bg-rose-200 transition-colors">
                      <LogOut className="w-4 h-4" />
                    </div>
                    <span className="text-xs sm:text-sm">Sign Out</span>
                  </div>

                  <ChevronRight className="w-4 h-4 text-rose-400 flex-shrink-0" />
                </button>
              </div>
            </div>

            {/* Bottom App Version / Brand Footer */}
            <div className="p-4 bg-gray-50 border-t border-pink-100 text-center text-[11px] text-gray-400 font-medium">
              BookMyEvents v2.4 • Bharath Developers
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
